/**
 * @ComponentFor InformationBlockViewModel
 */
import React, { useState } from 'react';
import { styled, StyledProps } from '@glitz/react';
import connect from 'Shared/connect';
import InformationBlock from './InformationBlockViewModel.type';
import { LinkButton, SolidButton, ButtonTheme } from 'Shared/TextButtons';
import { AutoSizeVideoBackground } from 'Shared/SharedComponents/molecules/ImageVideoBackground';
import ProductListItemViewModel from 'Product/ProductListing/ProductListItemViewModel.type';
import { H2, Paragraph, ResponsiveSize } from 'Shared/SharedComponents/atoms/Typography';
import { setImageBackground, Preset } from 'Shared/image-background';
import { GridWrap } from 'Shared/PageLayout';
import { createGridClasses, createOffsetGridClassLarge } from 'Shared/grid';
import { selectDeviceImage } from 'Shared/utils';
import { isCompact } from 'Shared/Viewport';
import * as style from 'Shared/Style';
import { iOSVersion } from '@polarnopyret/scope';
import QuickShopProductModal from 'Shared/QuickShopProductModal';

type ConnectedPropType = {
  currentBreakpoint: number;
  setDisplayedQuickShopProducts: (products?: ProductListItemViewModel[]) => void;
};

type PropType = InformationBlock & ConnectedPropType & StyledProps;

const paddingTop = 567;

const Background = styled(AutoSizeVideoBackground, {
  backgroundAttachment: 'fixed',
})

const ButtonWrap = styled.div({
  display: 'flex',
  gap: '32px 8px',
  marginTop: '16px',
  ['@media ' + style.mediaMinLarge]: {
    marginTop: '24px',
  },
})

const Heading = styled(H2, {
  letterSpacing: '0.01em',
  marginBottom: '16px',
  ['@media ' + style.mediaMinLarge]: {
    marginBottom: '24px',
  },
})

const Text = styled(Paragraph, {
  margin: { y: 0 },
})

const LinkedButton = styled(LinkButton, {
  textDecoration: 'none',
  display: 'inline-block',
})

function InformationBlock(props: PropType) {
  const {
    backgroundImage,
    backgroundImageSmallScreen,
    backgroundVideoSources,
    textColor,
    mainIntro,
    buttons,
    products,
    label,
    seeProductsButtonText,
  } = props;

  const [modalIsShown, setModalIsShown] = useState<boolean>(false);

  const textColorFromColorSystem =
    textColor === '#000000' ? style.colors.monochrome.black : style.colors.monochrome.white;
  const fadeColor =
    textColor !== '#000000' ? "0, 0, 0" : "255, 255, 255";

  const isMobile = isCompact(props.currentBreakpoint);
  const hasVideo = (backgroundVideoSources?.length ?? 0) > 0;
  const image = selectDeviceImage(backgroundImage, backgroundImageSmallScreen, isMobile);

  let IOS = false;
  try {
    const iOSver = iOSVersion().toString();
    if (iOSver.length > 1) {
      IOS = true;
    }
  } catch (error) {
  }

  return (
    <>
      <Background
        videoSources={backgroundVideoSources}
        breakPoint={props.currentBreakpoint}
        borderRadius={0}
        css={{
          ...(image && !hasVideo && {
            background: {
              image: setImageBackground(image?.url, Preset.FullWidth, `linear-gradient(180deg, rgba(${fadeColor}, 0) 35.28%, rgba(${fadeColor}, 0.2) 100%)`),
              // size: IOS ? '50% auto' : 'cover', // Will be 100% of the width and adjust the height automatically if it's an iOS device.
              size: IOS ? 'auto 100%' : 'cover',
              attachment: IOS ? 'scroll' : 'fixed',
              position: 'center center',
            },
          }),
        }}
      >
        <GridWrap
          className='grid'
          css={{
            padding: {
              top: `${paddingTop}px`,
              bottom: '24px',
            },
          }}
        >
          <div className={createGridClasses(12, 7, createOffsetGridClassLarge(2))}>
            <Heading css={{ color: textColorFromColorSystem, }} fontSize={ResponsiveSize.D14_M14}>
              {label}
            </Heading>
            <Text css={{ color: textColorFromColorSystem }} fontSize={ResponsiveSize.D56_M32} >
              {mainIntro}
            </Text>
            <ButtonWrap>
              {products?.length > 0 && (
                <SolidButton
                  text={seeProductsButtonText}
                  onClick={() => setModalIsShown(true)}
                />
              )}
              {buttons?.map((button, index) => (
                <LinkedButton
                  key={index}
                  to={button.url}
                  text={button.text}
                  title={button.text}
                  theme={
                    button.background === 'Transparent' ? ButtonTheme.TransparentWhite : ButtonTheme.WhiteToTransparent
                  }
                />
              ))}
            </ButtonWrap>
          </div>
        </GridWrap>
      </Background>
      <QuickShopProductModal isMobile={isMobile} modalIsShown={modalIsShown} hideModal={() => setModalIsShown(false)} products={products} />
    </>
  );
}

export default connect(
  (state) => ({
    currentBreakpoint: state.currentBreakpoint,
  }),
)(styled(InformationBlock));
